import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { fmtTimeAgo } from "@/lib/labels";
import { Plug, RefreshCw, CheckCircle2, AlertCircle, Power, Plus, Lock, Lock as LockIcon, Pencil, Trash2, Users } from "lucide-react";
import ConnectorForm from "@/components/ConnectorForm";

const STATUS_CLASS = { active: "b-healthy", error: "b-rev", disabled: "b-muted" };
// "sample data" only where the live flag decides it; a push connector takes in
// real deliveries either way.
const MODE_LABEL = { live: "live", sample: "sample data", push: "receives events" };

/**
 * Managing this workspace's sources.
 *
 * Lives here rather than in the page so the Sources screen and the Settings
 * tab render the same component. Two copies of a connector form is how the
 * previous one ended up unable to set SMTP.
 */
export default function SettingsConnectors() {
  const [connectors, setConnectors] = useState([]);
  const [kinds, setKinds] = useState([]);
  const [busy, setBusy] = useState({});
  const [msg, setMsg] = useState({});
  // Which kind is being added, or which connector is being edited. One at a
  // time: two open forms editing the same connector is a way to lose work.
  const [addingKind, setAddingKind] = useState(null);
  const [editing, setEditing] = useState(null);
  const [picking, setPicking] = useState(false);
  // Profile backfill progress per connector. Kept apart from `msg`, which
  // fades after a few seconds; a backfill runs for minutes and its progress
  // should stay on the card until it is finished.
  const [backfill, setBackfill] = useState({});
  const mounted = useRef(true);
  useEffect(() => () => { mounted.current = false; }, []);

  const load = () => api.get("/connectors").then((r) => setConnectors(r.data.connectors)).catch(() => {});
  useEffect(() => {
    load();
    // The server describes what can be connected and what each one needs, so
    // there is one description of a connector's settings rather than a copy
    // here that can drift from it.
    api.get("/connectors/kinds").then((r) => setKinds(r.data.kinds || [])).catch(() => {});
  }, []);

  // By capability rather than by type, so every kind that looks customers up
  // (the generic lookup, HubSpot) offers the backfill.
  const looksUpCustomers = (c) =>
    kinds.some((k) => k.kind === c.type && k.capabilities?.includes("profile_lookup"));

  const flash = (id, text) => {
    setMsg((m) => ({ ...m, [id]: text }));
    setTimeout(() => setMsg((m) => ({ ...m, [id]: "" })), 6000);
  };

  const withBusy = async (id, fn) => {
    setBusy((b) => ({ ...b, [id]: true }));
    try { await fn(); } finally { setBusy((b) => ({ ...b, [id]: false })); }
  };

  const sync = (c) => withBusy(c.id, async () => {
    try {
      const { data } = await api.post(`/connectors/${c.id}/sync`);
      // A failed sync is a connector-health result, not a request error.
      flash(c.id, data.status === "failed"
        ? `Sync failed — ${data.error}`
        : `Fetched ${data.fetched}, ingested ${data.ingested}, ${data.duplicates} already seen.`);
      await load();
    } catch (e) {
      flash(c.id, e?.response?.data?.message || "Sync failed");
    }
  });

  // Follows a backfill the worker is running until it finishes. Polling rather
  // than holding a request open: the work happens in the background, in
  // batches, and may take a while for a large workspace.
  const followBackfill = async (id) => {
    for (;;) {
      if (!mounted.current) return;
      try {
        const { data } = await api.get(`/connectors/${id}/backfill-profiles`);
        if (!mounted.current) return;
        setBackfill((b) => ({ ...b, [id]: data.backfill }));
        if (!data.backfill || data.backfill.status !== "running") return;
      } catch {
        return;
      }
      await new Promise((resolve) => setTimeout(resolve, 3000));
    }
  };

  const startBackfill = (c) => withBusy(c.id, async () => {
    try {
      const { data } = await api.post(`/connectors/${c.id}/backfill-profiles`);
      setBackfill((b) => ({ ...b, [c.id]: data }));
      followBackfill(c.id);
    } catch (e) {
      flash(c.id, e?.response?.data?.message || "Could not start looking customers up");
    }
  });

  const toggle = (c) => withBusy(c.id, async () => {
    const next = c.status === "active" ? "disabled" : "active";
    try {
      await api.post(`/connectors/${c.id}/status`, { status: next });
      await load();
      flash(c.id, `Connector ${next}.`);
    } catch (e) {
      flash(c.id, e?.response?.data?.message || "Could not change status");
    }
  });

  const edit = (c) => withBusy(c.id, async () => {
    try {
      // The full record, including settings: the list view deliberately never
      // returns config, so the form asks for it rather than showing blanks.
      const { data } = await api.get(`/connectors/${c.id}`);
      setEditing(data);
      setAddingKind(null);
      setPicking(false);
    } catch (e) {
      flash(c.id, e?.response?.data?.message || "Could not open this connector");
    }
  });

  const remove = (c) => {
    if (!window.confirm(
      `Delete "${c.name}"?\n\n` +
      "Its stored credentials are forgotten. Signals already imported stay; " +
      "nothing new arrives from this source."
    )) return;
    return withBusy(c.id, async () => {
      try {
        await api.delete(`/connectors/${c.id}`);
        await load();
      } catch (e) {
        flash(c.id, e?.response?.data?.message || "Could not delete this connector");
      }
    });
  };

  const onSaved = async () => {
    setAddingKind(null);
    setEditing(null);
    await load();
  };

  return (
    <div>
      <div className="flex justify-end mb-4">
        <button className="btn" data-testid="add-connector"
                onClick={() => { setPicking((v) => !v); setEditing(null); setAddingKind(null); }}>
          <Plus size={13} /> Add source
        </button>
      </div>

      {picking && (
        <div className="card p-5 mb-5" data-testid="kind-picker">
          <div className="text-[11px] uppercase tracking-wider mb-3" style={{ color: "var(--text-muted)" }}>
            What would you like to connect?
          </div>
          <div className="grid grid-cols-12 gap-3">
            {kinds.map((k) => {
              // Locked kinds are shown rather than hidden: knowing a capability
              // exists and what unlocks it is more useful than an option that
              // silently is not there. The server enforces it regardless.
              const usable = k.allowed && k.available;
              return (
                <button
                  key={k.kind}
                  type="button"
                  disabled={!usable}
                  data-testid={`kind-${k.kind}`}
                  onClick={() => { setAddingKind(k); setPicking(false); }}
                  className="col-span-12 md:col-span-6 text-left p-4 rounded-lg disabled:opacity-50"
                  style={{ background: "var(--surface-2)", border: "1px solid var(--border)" }}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-medium text-[13px]">{k.label}</span>
                    {!k.allowed && (
                      <span className="badge b-sla">
                        <LockIcon size={9} className="inline mr-1" />
                        needs {k.requires ? k.requires.replace(/_/g, " ") : "a higher plan"}
                      </span>
                    )}
                    {k.allowed && !k.available && <span className="badge b-muted">coming soon</span>}
                  </div>
                  <p className="text-[12px] mt-1" style={{ color: "var(--text-secondary)" }}>
                    {k.description}
                  </p>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {addingKind && (
        <div className="mb-5">
          <ConnectorForm kind={addingKind} onSaved={onSaved} onCancel={() => setAddingKind(null)} />
        </div>
      )}

      {editing && (
        <div className="mb-5">
          <ConnectorForm kind={editing.kind} connector={editing}
                         onSaved={onSaved} onCancel={() => setEditing(null)} />
        </div>
      )}

      <div className="grid grid-cols-12 gap-4" data-testid="connectors-grid">
        {connectors.length === 0 && (
          <div className="col-span-12 card p-10 text-center" style={{ color: "var(--text-muted)" }}>
            <Plug size={20} className="inline mb-2" /><br />
            No sources connected yet.
          </div>
        )}
        {connectors.map((c) => (
          <div key={c.id} className="card p-5 col-span-12 md:col-span-6" data-testid={`connector-${c.type}`}>
            <div className="flex items-start justify-between gap-2 mb-2">
              <div>
                <div className="font-medium text-[13.5px]">{c.name}</div>
                <div className="text-[10.5px] font-mono mt-0.5" style={{ color: "var(--text-muted)" }}>
                  {c.kind} · {MODE_LABEL[c.mode] || c.mode}
                  {c.has_credentials && <> · <Lock size={9} className="inline" /> credentials stored</>}
                </div>
              </div>
              <span className={`badge ${STATUS_CLASS[c.status] || "b-muted"}`}>
                {c.status === "active" ? <CheckCircle2 size={11} className="inline mr-0.5" />
                  : c.status === "error" ? <AlertCircle size={11} className="inline mr-0.5" /> : null}
                {c.status}
              </span>
            </div>

            <div className="grid grid-cols-3 gap-2 mt-3 text-center">
              <Stat label="Imported" value={c.signals_imported} />
              <Stat label="Failures" value={c.consecutive_failures} bad={c.consecutive_failures > 0} />
              <Stat label="Last sync" value={fmtTimeAgo(c.last_sync_at) || "never"} small />
            </div>

            {c.last_error && (
              <div className="text-[11px] mt-2.5 leading-snug" style={{ color: "var(--risk-rev)" }}>
                {c.last_error}
              </div>
            )}
            {c.cursor?.last_uid !== undefined && (
              <div className="font-mono text-[10px] mt-2" style={{ color: "var(--text-muted)" }}>
                checkpoint: uid {c.cursor.last_uid}
              </div>
            )}
            {msg[c.id] && <div className="badge b-muted mt-2">{msg[c.id]}</div>}
            {backfill[c.id] && (
              <div className="text-[11px] mt-2 leading-snug" data-testid={`backfill-${c.type}`}
                   style={{ color: "var(--text-secondary)" }}>
                {describeBackfill(backfill[c.id])}
              </div>
            )}

            <div className="flex gap-1.5 mt-3">
              {/* Only connectors that fetch have anything to sync. A profile
                  lookup is asked on demand and a webhook is pushed to us. */}
              {c.can_sync !== false && (
                <button className="btn btn-ghost" style={{ padding: "4px 10px", fontSize: 11 }}
                        onClick={() => sync(c)} disabled={busy[c.id]} data-testid={`sync-${c.type}`}>
                  <RefreshCw size={11} /> Sync now
                </button>
              )}
              {looksUpCustomers(c) && (
                <button className="btn btn-ghost" style={{ padding: "4px 10px", fontSize: 11 }}
                        onClick={() => startBackfill(c)}
                        disabled={busy[c.id] || backfill[c.id]?.status === "running"}
                        title="Look up every customer who has an open case"
                        data-testid={`backfill-start-${c.type}`}>
                  <Users size={11} /> Look up existing customers
                </button>
              )}
              <button className="btn btn-ghost" style={{ padding: "4px 10px", fontSize: 11 }}
                      onClick={() => toggle(c)} disabled={busy[c.id]}>
                <Power size={11} /> {c.status === "active" ? "Disable" : "Enable"}
              </button>
              <button className="btn btn-ghost" style={{ padding: "4px 10px", fontSize: 11 }}
                      onClick={() => edit(c)} disabled={busy[c.id]}
                      data-testid={`edit-${c.type}`}>
                <Pencil size={11} /> Edit
              </button>
              <button className="btn btn-ghost" style={{ padding: "4px 10px", fontSize: 11 }}
                      onClick={() => remove(c)} disabled={busy[c.id]}
                      data-testid={`delete-${c.type}`}>
                <Trash2 size={11} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const Stat = ({ label, value, bad, small }) => (
  <div className="px-2 py-2 rounded-md" style={{ background: "var(--surface-2)", border: "1px solid var(--border)" }}>
    <div className="text-[10px] uppercase tracking-wider mb-0.5" style={{ color: "var(--text-muted)" }}>{label}</div>
    <div className={small ? "text-[11.5px]" : "font-mono text-[16px]"}
         style={bad ? { color: "var(--risk-rev)" } : {}}>{value}</div>
  </div>
);

/** One line of backfill progress, in words an operator can act on. */
export function describeBackfill(b) {
  const counts = `${b.found} found, ${b.not_found} not found` +
    (b.skipped ? `, ${b.skipped} skipped` : "") +
    (b.unanswered ? `, ${b.unanswered} unanswered` : "") +
    (b.conflicts ? `, ${b.conflicts} possible duplicates` : "");
  if (b.status === "running") {
    const done = b.looked_up + b.skipped + b.unanswered;
    const of = b.candidates != null ? ` of ${b.candidates}` : "";
    const stalled = b.stalled
      ? " Nothing has been processed for several minutes — the background worker" +
        " is probably not running. It resumes where it stopped once it is."
      : "";
    return `Looking customers up: ${done}${of} done — ${counts}.${stalled}`;
  }
  const hint = b.unanswered
    ? " Unanswered lookups mean the tenant's API failed or refused; press Test to see why."
    : "";
  return `Lookup of existing customers ${b.status === "succeeded" ? "finished" : b.status}: ` +
    `${b.looked_up} looked up — ${counts}.${hint}`;
}
